import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { RefreshCw, FileText, Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

interface DataRoomVersioningProps {
  companyId: string;
}

interface FileData {
  id: string;
  file_name: string;
  category: string;
  version: number;
  summary: string | null;
  created_at: string;
}

export default function DataRoomVersioning({ companyId }: DataRoomVersioningProps) {
  const [files, setFiles] = useState<FileData[]>([]);
  const [loading, setLoading] = useState(true);
  const [summarizingIds, setSummarizingIds] = useState<Set<string>>(new Set());

  async function loadFiles() {
    setLoading(true);
    const { data, error } = await supabase
      .from('data_room_files')
      .select('id, file_name, category, version, summary, created_at')
      .eq('company_id', companyId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error loading files:', error);
      toast.error(error.message);
    }
    setFiles(data || []);
    setLoading(false);
  }

  async function summarize(fileId: string) {
    setSummarizingIds((prev) => new Set(prev).add(fileId));
    toast.info('Ella is analyzing and summarizing the document...');

    try {
      const { data, error } = await supabase.functions.invoke('ella-summarize-file', {
        body: { fileId },
      });

      if (error) throw error;

      toast.success('Document summary generated successfully');
      loadFiles();
    } catch (error: any) {
      console.error('Error summarizing file:', error);
      toast.error(error.message || 'Failed to generate summary');
    } finally {
      setSummarizingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(fileId);
        return newSet;
      });
    }
  }

  useEffect(() => {
    loadFiles();
  }, [companyId]);

  return (
    <Card className="border-border bg-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="h-5 w-5" />
          Document Versions & AI Summaries
        </CardTitle>
        <Button variant="outline" size="sm" onClick={loadFiles}>
          <RefreshCw className="h-4 w-4 mr-1" />
          Refresh
        </Button>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        {loading ? (
          <div className="p-8 flex justify-center">
            <Loader2 className="animate-spin text-muted-foreground" />
          </div>
        ) : files.length ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>File</TableHead>
                <TableHead>Version</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>AI Summary</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {files.map((f) => (
                <TableRow key={f.id}>
                  <TableCell className="font-medium">{f.file_name}</TableCell>
                  <TableCell>v{f.version}</TableCell>
                  <TableCell>{f.category}</TableCell>
                  <TableCell className="max-w-[400px]">
                    {f.summary ? (
                      <p className="text-sm text-muted-foreground line-clamp-2">{f.summary}</p>
                    ) : (
                      <p className="text-sm text-muted-foreground italic">Not yet summarized</p>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => summarize(f.id)}
                      disabled={summarizingIds.has(f.id)}
                    >
                      {summarizingIds.has(f.id) ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                          Analyzing...
                        </>
                      ) : (
                        <>
                          <FileText className="h-4 w-4 mr-1" />
                          Summarize
                        </>
                      )}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <p className="text-sm text-muted-foreground text-center py-8">
            No files uploaded yet. Upload files to see them here with AI-powered summaries.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
